import React, { useCallback, useState } from 'react';

import Tooltip from 'component/Layout/Tooltip';

import classNames from 'classnames';

import { useOutsideClick } from 'hooks/useOutsideClick';

import { ReactComponent as IconInfoCircle } from 'media/svg/infoCircle.svg';

import styles from './Custom.module.scss';

export interface OptionListProps<Value extends string = string> {
    value: Value;
    label: string;
    tooltip?: string;
}

export interface CustomProps<Value extends string = string> {
    currentValue?: Value;
    customClass?: {
        SelectWrapper?: string;
        Select?: string;
        Dropdown?: string;
        Option?: string;
        Selected?: string;
    };
    onClick?: (value: Value) => void;
    optionList: OptionListProps<Value>[];
}

const Custom = <Value extends string = string>({
    currentValue,
    optionList,
    onClick,
    customClass,
}: CustomProps<Value>) => {
    const sortLayerRef = useOutsideClick<HTMLDivElement>(() =>
        toggleSelect(false)
    );
    const [open, setOpen] = useState(false);
    const toggleSelect = useCallback((value = !open) => setOpen(value), [open]);
    const current = optionList?.find((option) => option.value === currentValue);

    return (
        <div
            className={classNames(
                styles.SelectWrapper,
                customClass?.SelectWrapper
            )}
            tabIndex={-1}
            ref={sortLayerRef}
        >
            <div
                className={classNames(styles.Select, customClass?.Select)}
                onClick={() => toggleSelect()}
            >
                {current?.label}
                {current?.tooltip && (
                    <Tooltip
                        content={current?.tooltip}
                        align={{
                            offset: [15, 0],
                        }}
                    >
                        <span className={styles.TooltipIcon}>
                            <IconInfoCircle />
                        </span>
                    </Tooltip>
                )}
            </div>
            {open && (
                <div
                    className={classNames(
                        styles.Dropdown,
                        customClass?.Dropdown
                    )}
                >
                    {optionList?.map((value, key) => (
                        <div
                            onClick={() => {
                                onClick?.(value.value);
                                toggleSelect(false);
                            }}
                            className={classNames(
                                styles.Option,
                                customClass?.Option,
                                {
                                    [styles.Selected]:
                                        current?.value === value.value,
                                    [customClass?.Selected ?? '']:
                                        current?.value === value.value,
                                }
                            )}
                            key={key}
                            role={'presentation'}
                        >
                            {value.label}
                            {value?.tooltip && (
                                <Tooltip
                                    content={value?.tooltip}
                                    align={{
                                        offset: [15, 0],
                                    }}
                                >
                                    <span className={styles.TooltipIcon}>
                                        <IconInfoCircle />
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Custom;
